import {
  BaseApi,
  type ItemResponse,
  type RequestParams,
  type RequestHeaders,
  type PaginatedResponse,
} from '@bv/api/base';
import { type Cdr, type Call, useSipStore } from '@bv/stores';
import { uuidV4 } from '@bv/utils';

/**
 * @interface CallLog
 * Represents the detailed structure of a single call log entry.
 */
export interface CallLog {
  direction: string;
  phone_number: string | number;
  identifier: string;
  status: string;
  call_time: string;
  cdr: Cdr;
  id?: string;
  user_id?: string;
  workspace_id?: string;
  contact_id?: string;
  contact?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * @interface CallLogOverview
 * Represents a summarized version of a call log, suitable for list views.
 */
interface CallLogOverview {
  phone_number: string;
  call_time: string;
  status: string;
  direction: string;
  contact_id: string | null;
  total: number;
  contact_name: string;
}

/**
 * Manages API requests related to voip call logs.
 */
export class CallLogApi extends BaseApi {
  /**
   * Initializes a new instance of the CallLogApi.
   * The base endpoint is set to 'contacts'.
   */
  public constructor() {
    super('contacts');
  }

  /**
   * Fetches a paginated list of call log overviews.
   * @param {object} [options={}] - Optional request configurations.
   * @param {RequestParams} [options.params] - Query parameters for the request.
   * @param {RequestHeaders} [options.headers] - Custom headers for the request.
   * @returns {Promise<PaginatedResponse<CallLogOverview>>} A promise that resolves to the paginated list of call logs.
   */
  public async paginated({
    params,
    headers,
  }: {
    params?: RequestParams;
    headers?: RequestHeaders;
  }): Promise<PaginatedResponse<CallLogOverview>> {
    return this.request<CallLogOverview, PaginatedResponse<CallLogOverview>>({
      method: 'get',
      url: 'logs-overview',
      params,
      headers,
    });
  }

  /**
   * Creates a new call log entry from a Call object.
   * @param {Call} call - The core call data to create the log from.
   * @param {object} [options={}] - Optional request configurations.
   * @param {RequestParams} [options.params] - Query parameters for the request.
   * @param {RequestHeaders} [options.headers] - Custom headers for the request.
   * @returns {Promise<CallLog>} A promise that resolves to the newly created call log.
   */
  public async create({
    call,
    params,
    headers,
  }: {
    call: Call;
    params?: RequestParams;
    headers?: RequestHeaders;
  }): Promise<CallLog> {
    const data: CallLog = {
      direction: call.cdr.direction,
      phone_number: call.cdr.direction === 'inbound' ? call.cdr.fromNumber : call.cdr.toNumber,
      identifier: uuidV4(),
      call_time: call.cdr.startTime,
      status: call.cdr.status,
      cdr: call.cdr,
    };
    return this.request<CallLog, ItemResponse<CallLog>>({
      method: 'post',
      url: 'logs',
      data,
      params,
      headers,
    }).then((response) => {
      useSipStore().setRecentlyAddedLog(response.data);
      return response.data;
    });
  }
}

/**
 * A singleton instance of the CallLogApi class for convenient access.
 */
export const callLogApi: CallLogApi = new CallLogApi();
